/**
 * Scroll-triggered reveal (one-shot CSS transitions, not scroll-scrubbed).
 *
 * - Fade in: when the element's top crosses the enter line.
 * - Fade out: when the element's bottom crosses the exit line.
 *
 * Viewport defaults (unless data-scroll-reveal-enter-at / exit-at on element):
 *   Desktop (≥1000px): top ≤ 75% vh and bottom > 25% vh → visible (combined band).
 *   Mobile (<1000px):  rect.top < 80% vh → show; rect.top ≥ 80% vh → hide;
 *                      rect.bottom > 20% vh → show; rect.bottom ≤ 20% vh → hide.
 *   (Mobile uses enter-at as top 80% line, exit-at as bottom 20% line from viewport top.)
 *
 * Usage:
 *   <div scroll-reveal>…</div>
 *   <div scroll-reveal="fade">…</div>
 *
 * Stagger (Apple-style row sequence, parent triggers children):
 *   <div scroll-reveal-stagger="fade-slide-up" data-scroll-reveal-stagger-step="90">
 *     <div class="card">…</div>
 *   </div>
 *   data-scroll-reveal-stagger-child=".card" — optional child selector
 *   data-scroll-reveal-stagger-enter-only="true" — fade in once, never fade out on scroll
 *
 * Per-element overrides:
 *   data-scroll-reveal-enter-at="0.75"
 *   data-scroll-reveal-exit-at="0.25"
 *   data-scroll-reveal-enter-only="true" — fade in once, never fade out on scroll
 *   data-scroll-reveal-duration="600"
 *   data-scroll-reveal-distance="32"
 */
(function () {
  const MOBILE_MQ = window.matchMedia("(max-width: 999px)");

  const SCROLL_REVEAL_DEFAULTS = {
    duration: 600,
    distance: 32,
  };

  const STAGGER_DEFAULTS = {
    duration: 900,
    distance: 28,
    step: 140,
  };

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return;

  const elements = new Set();
  /** @type {Set<{ container: Element, children: Element[], options: object }>} */
  const staggerGroups = new Set();
  let ticking = false;

  const CLASS_VISIBLE = "is-scroll-reveal-visible";
  const CLASS_EXIT = "is-scroll-reveal-out";

  function parseNumber(value, fallback) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function clamp01(n) {
    return Math.max(0, Math.min(1, n));
  }

  function getViewportDefaults() {
    if (MOBILE_MQ.matches) {
      return { enterAt: 0.85, exitAt: 0.1 };
    }
    return { enterAt: 0.85, exitAt: 0.2 };
  }

  function getPreset(el) {
    const attr = el.getAttribute("scroll-reveal");
    if (attr === "fade") return "fade";
    if (el.getAttribute("fade-on-scroll-out") === "true") return "fade";
    return "fade-slide-up";
  }

  function getStaggerPreset(container) {
    const attr = container.getAttribute("scroll-reveal-stagger");
    if (!attr || attr === "true") return "fade-slide-up";
    if (attr === "fade") return "fade";
    return attr;
  }

  function getOptions(el) {
    const viewport = getViewportDefaults();
    const enterFallback =
      el.dataset.scrollRevealJourney != null
        ? parseNumber(el.dataset.scrollRevealJourney, viewport.enterAt)
        : viewport.enterAt;

    return {
      preset: getPreset(el),
      enterAt: clamp01(parseNumber(el.dataset.scrollRevealEnterAt, enterFallback)),
      exitAt: clamp01(parseNumber(el.dataset.scrollRevealExitAt, viewport.exitAt)),
      duration: Math.max(0, parseNumber(el.dataset.scrollRevealDuration, SCROLL_REVEAL_DEFAULTS.duration)),
      distance: parseNumber(el.dataset.scrollRevealDistance, SCROLL_REVEAL_DEFAULTS.distance),
      enterOnly: el.dataset.scrollRevealEnterOnly === "true",
    };
  }

  function shouldSkipExit(el, options) {
    return options.enterOnly && getState(el) === "visible";
  }

  function getStaggerOptions(container) {
    const viewport = getViewportDefaults();

    return {
      preset: getStaggerPreset(container),
      enterAt: clamp01(parseNumber(container.dataset.scrollRevealEnterAt, viewport.enterAt)),
      exitAt: clamp01(parseNumber(container.dataset.scrollRevealExitAt, viewport.exitAt)),
      duration: Math.max(
        0,
        parseNumber(container.dataset.scrollRevealDuration, STAGGER_DEFAULTS.duration)
      ),
      distance: parseNumber(container.dataset.scrollRevealDistance, STAGGER_DEFAULTS.distance),
      step: Math.max(0, parseInt(container.dataset.scrollRevealStaggerStep, 10) || STAGGER_DEFAULTS.step),
      enterOnly: container.dataset.scrollRevealStaggerEnterOnly === "true",
    };
  }

  function shouldSkipStaggerExit(container, options) {
    return options.enterOnly && container.dataset.scrollRevealStaggerState === "visible";
  }

  function applyCssVars(el, options) {
    el.style.setProperty("--scroll-reveal-duration", `${options.duration}ms`);
    el.style.setProperty("--scroll-reveal-distance", `${options.distance}px`);
    if (options.preset === "fade") {
      el.style.setProperty("--scroll-reveal-distance", "0px");
    }
  }

  function applyStaggerItemVars(el, options, index, step) {
    applyCssVars(el, options);

    if (options.preset === "fade") {
      el.setAttribute("scroll-reveal", "fade");
    } else if (options.preset && options.preset !== "fade-slide-up") {
      el.setAttribute("scroll-reveal", options.preset);
    } else {
      el.setAttribute("scroll-reveal", "");
    }

    el.style.setProperty("--scroll-reveal-delay", `${index * step}ms`);
  }

  function getState(el) {
    return el.dataset.scrollRevealState || "hidden";
  }

  function setState(el, state) {
    el.dataset.scrollRevealState = state;

    if (state === "visible") {
      el.classList.add(CLASS_VISIBLE);
      el.classList.remove(CLASS_EXIT);
      el.style.setProperty("--scroll-progress", "1");
      return;
    }

    if (state === "exit" || state === "gone") {
      el.classList.remove(CLASS_VISIBLE);
      el.classList.add(CLASS_EXIT);
      el.style.setProperty("--scroll-progress", "0");
      el.style.setProperty("--scroll-reveal-delay", "0ms");
      return;
    }

    el.classList.remove(CLASS_VISIBLE, CLASS_EXIT);
    el.style.setProperty("--scroll-progress", "0");
    el.style.setProperty("--scroll-reveal-delay", "0ms");
  }

  function isMobileMode() {
    return MOBILE_MQ.matches;
  }

  function getLines(vh, enterAt, exitAt) {
    return {
      topLine: vh * enterAt,
      bottomLine: vh * exitAt,
    };
  }

  /**
   * Mobile: two independent checks (both must pass to show).
   * - top:    show if rect.top < topAt·vh;    hide if rect.top ≥ topAt·vh
   * - bottom: show if rect.bottom > bottomAt·vh; hide if rect.bottom ≤ bottomAt·vh
   */
  function evaluateMobileRect(state, rect, vh, topAt, bottomAt, handlers) {
    if (rect.top > vh || rect.bottom < 0) {
      if (state !== "hidden") handlers.setHidden();
      return;
    }

    const topMaxLine = vh * topAt;
    const bottomMinLine = vh * bottomAt;
    const topOk = rect.top < topMaxLine;
    const bottomOk = rect.bottom > bottomMinLine;
    const canShow = topOk && bottomOk;

    if (state === "hidden" || state === "gone") {
      if (canShow) handlers.setVisible();
      return;
    }

    if (state === "visible") {
      if (!canShow) {
        handlers.setExit();
      }
      return;
    }

    if (state === "exit") {
      if (canShow) {
        handlers.setVisible();
      }
    }
  }

  /** Desktop: top + bottom both define the visible band. */
  function evaluateDesktopRect(state, rect, vh, topLine, bottomLine, handlers) {
    if (rect.top > vh || rect.bottom < 0) {
      if (state !== "hidden") handlers.setHidden();
      return;
    }

    if (state === "hidden" || state === "gone") {
      if (rect.top <= topLine && rect.bottom > bottomLine) {
        handlers.setVisible();
      }
      return;
    }

    if (state === "visible") {
      if (rect.bottom <= bottomLine) {
        handlers.setExit();
      } else if (rect.top > topLine) {
        handlers.setHidden();
      }
      return;
    }

    if (state === "exit") {
      if (rect.bottom > bottomLine && rect.top <= topLine) {
        handlers.setVisible();
      } else if (rect.top > topLine) {
        handlers.setHidden();
      }
    }
  }

  function evaluate(el) {
    const options = getOptions(el);
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const { topLine, bottomLine } = getLines(vh, options.enterAt, options.exitAt);
    const state = getState(el);
    const handlers = {
      setVisible: () => setState(el, "visible"),
      setHidden: () => {
        if (shouldSkipExit(el, options)) return;
        setState(el, "hidden");
      },
      setExit: () => {
        if (shouldSkipExit(el, options)) return;
        setState(el, "exit");
      },
    };

    if (isMobileMode()) {
      evaluateMobileRect(state, rect, vh, options.enterAt, options.exitAt, handlers);
    } else {
      evaluateDesktopRect(state, rect, vh, topLine, bottomLine, handlers);
    }
  }

  function revealStaggerChildren(children, options) {
    children.forEach((child, index) => {
      child.style.setProperty("--scroll-reveal-delay", `${index * options.step}ms`);
      setState(child, "visible");
    });
  }

  function hideStaggerChildren(children) {
    children.forEach((child) => setState(child, "hidden"));
  }

  function exitStaggerChildren(children) {
    children.forEach((child) => setState(child, "exit"));
  }

  function evaluateStagger(group) {
    const { container, children, options } = group;
    const rect = container.getBoundingClientRect();
    const vh = window.innerHeight;
    const { topLine, bottomLine } = getLines(vh, options.enterAt, options.exitAt);
    const state = container.dataset.scrollRevealStaggerState || "hidden";

    const handlers = {
      setVisible: () => {
        if (container.dataset.scrollRevealStaggerState === "visible") return;

        container.dataset.scrollRevealStaggerState = "visible";
        revealStaggerChildren(children, options);
      },
      setHidden: () => {
        if (shouldSkipStaggerExit(container, options)) return;

        container.dataset.scrollRevealStaggerState = "hidden";
        hideStaggerChildren(children);
      },
      setExit: () => {
        if (shouldSkipStaggerExit(container, options)) return;

        container.dataset.scrollRevealStaggerState = "exit";
        exitStaggerChildren(children);
      },
    };

    if (isMobileMode()) {
      evaluateMobileRect(state, rect, vh, options.enterAt, options.exitAt, handlers);
    } else {
      evaluateDesktopRect(state, rect, vh, topLine, bottomLine, handlers);
    }
  }

  function bind(el) {
    if (el.dataset.scrollRevealInit === "true") return;
    el.dataset.scrollRevealInit = "true";

    const options = getOptions(el);
    applyCssVars(el, options);
    setState(el, "hidden");
    elements.add(el);
    evaluate(el);
  }

  function bindStagger(container) {
    if (container.dataset.scrollRevealStaggerInit === "true") return;
    container.dataset.scrollRevealStaggerInit = "true";

    const options = getStaggerOptions(container);
    const selector = container.dataset.scrollRevealStaggerChild;
    const children = selector
      ? [...container.querySelectorAll(selector)]
      : [...container.children].filter((node) => node.nodeType === 1);

    if (!children.length) return;

    children.forEach((child, index) => {
      if (child.dataset.scrollRevealStaggerItem === "true") return;
      child.dataset.scrollRevealStaggerItem = "true";
      applyStaggerItemVars(child, options, index, options.step);
      setState(child, "hidden");
    });

    container.dataset.scrollRevealStaggerState = "hidden";
    staggerGroups.add({ container, children, options });
    evaluateStagger({ container, children, options });
  }

  function onScrollOrResize() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      elements.forEach(evaluate);
      staggerGroups.forEach(evaluateStagger);
      ticking = false;
    });
  }

  function init(root) {
    const scope = root && root.nodeType ? root : document;

    staggerGroups.forEach((group) => {
      if (!group.container.isConnected) staggerGroups.delete(group);
    });

    scope.querySelectorAll("[scroll-reveal], [fade-on-scroll-out=\"true\"]").forEach(bind);
    scope.querySelectorAll("[scroll-reveal-stagger]").forEach(bindStagger);
    onScrollOrResize();
  }

  window.initScrollReveal = init;

  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize, { passive: true });

  if (typeof MOBILE_MQ.addEventListener === "function") {
    MOBILE_MQ.addEventListener("change", onScrollOrResize);
  } else if (typeof MOBILE_MQ.addListener === "function") {
    MOBILE_MQ.addListener(onScrollOrResize);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target || document);
  });
})();
